import { api, putToSignedUrl } from '../api';
import type { Media, MediaContent, MediaType, FeaturedStatus, UploadInitResponse, UploadCompleteResponse, Page } from '../../types/api';

export interface SearchResp { data: Media[]; page: number; totalCount?: number; totalPages?: number; }

export const MediaAPI = {
  search: (param: string, page: number, type?: MediaType) =>
    api.post<SearchResp>('/api/media/search', type ? { param, page, type } : { param, page }),
  get: (id: string) =>
    api.post<Media>('/api/media/get', { id }),
  // Admin-only plaintext transcript + long descriptions. /media/get strips these
  // (clients receive them encrypted over playback); the editor preloads them here so
  // saving one section never wipes the others. Works for any status, no subscription.
  content: (id: string) =>
    api.post<MediaContent>('/api/media/content', { id }),
  save: (media: Partial<Media>) =>
    api.post<Media>('/api/media/save', media, { idempotent: true }),
  delete: (id: string) =>
    api.post<void>('/api/media/delete', { id }),
  // Publish and unpublish are distinct backend endpoints. The body only carries
  // the id; there is no `publish` flag server-side (MediaRequest ignores it), so
  // posting to /publish with publish:false would silently re-publish.
  publish: (id: string) =>
    api.post<void>('/api/media/publish', { id }, { idempotent: true }),
  unpublish: (id: string) =>
    api.post<void>('/api/media/unpublish', { id }, { idempotent: true }),
  saveTranscriptEn: (payload: { id: string; transcript: unknown }) =>
    api.post<Media>('/api/media/transcript/en', payload, { idempotent: true }),
  // featured=true pins the item (pauses auto-rotation); false un-pins (resumes).
  feature: (id: string, featured: boolean) =>
    api.post<Media>('/api/media/feature', { id, featured }, { idempotent: true }),
  // Automatic 24h featured rotation controls
  featuredStatus: () =>
    api.get<FeaturedStatus>('/api/media/featured/status'),
  rotateFeatured: () =>
    api.post<FeaturedStatus>('/api/media/featured/rotate', {}, { idempotent: true }),
};

export interface InitUploadRequest {
  filename: string;
  fileType: string;
  numberOfParts: number;
  mediaType: 'AUDIO' | 'VIDEO';
}

export interface CompleteUploadRequest {
  mediaId: string;
  filename: string;
  fileType: string;
  uploadId: string;
  mediaType: 'AUDIO' | 'VIDEO';
  partETags: { partNumber: number; etag: string }[];
}

export const UploadAPI = {
  init: (req: InitUploadRequest) =>
    api.post<UploadInitResponse>('/api/upload/multipart/start', req),
  complete: (req: CompleteUploadRequest) =>
    api.post<UploadCompleteResponse>('/api/upload/multipart/complete', req),
  putPart: putToSignedUrl,
};
